import { useRef, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  createInitialSteps,
  invalidateDownstreamSteps,
  resolvePipelineStepInput,
} from '~/app/components/pipeline-debug/PipelineDebug/hooks/pipeline-step-state.utils';
import {
  endExclusiveRun,
  isAnyPipelineStepRunning,
  tryBeginExclusiveRun,
} from '~/app/components/pipeline-debug/PipelineDebug/hooks/pipeline-run-lock';
import { createStepStreamHandlers } from '~/app/components/pipeline-debug/PipelineDebug/hooks/pipeline-step-stream.handlers';
import type {
  ParsedJsonUpload,
  PipelineRunMode,
  PipelineStepId,
  PipelineStepState,
} from '~/app/components/pipeline-debug/PipelineDebug/types';
import { PIPELINE_STEP_IDS } from '~/app/components/pipeline-debug/PipelineDebug/types';
import {
  parseJsonFileContent,
  summarizeStepOutput,
  validateStepInput,
} from '~/app/components/pipeline-debug/PipelineDebug/utils';
import {
  clearSupervisorSandbox,
  runSupervisorPipelineStepStream,
  uploadSupervisorManualLog,
} from '~/app/services/manualUpload/supervisor-manual-upload.service';
import type { SupervisorManualUploadResponse } from '../types';
import { buildSupervisorLinUploadFile } from '../utils';

function toJsonSafeValue(value: unknown): unknown {
  return JSON.parse(JSON.stringify(value));
}

function readLogId(log: unknown, fallbackIndex: number): string | undefined {
  if (log !== null && typeof log === 'object' && !Array.isArray(log)) {
    const record = log as Record<string, unknown>;
    const logId = record.logId ?? record.log_id;
    if (typeof logId === 'string' && logId.trim() !== '') {
      return logId;
    }
  }

  return `log-${fallbackIndex + 1}`;
}

export function useSupervisorManualUploadPipeline() {
  const { t } = useTranslation(['manualUpload', 'pipelineDebug']);

  const [runMode] = useState<PipelineRunMode>('step-by-step');
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [uploads, setUploads] = useState<ParsedJsonUpload[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [steps, setSteps] =
    useState<Record<PipelineStepId, PipelineStepState>>(createInitialSteps);
  const [result, setResult] = useState<SupervisorManualUploadResponse | null>(
    null,
  );
  const [isResetting, setIsResetting] = useState(false);
  const uploadsRef = useRef(uploads);
  const selectedFileRef = useRef(selectedFile);
  const runInFlightRef = useRef(false);

  uploadsRef.current = uploads;
  selectedFileRef.current = selectedFile;

  const activeStepId = PIPELINE_STEP_IDS[activeStepIndex];
  const activeStep = steps[activeStepId];
  const isBusy = isResetting || isAnyPipelineStepRunning(steps);

  const setStepInput = (
    stepId: PipelineStepId,
    input: unknown,
    notify = true,
  ): void => {
    if (stepId === 'lin') {
      setResult(null);
    }

    setSteps((current) => {
      const validation = validateStepInput(stepId, input);

      const nextStep: PipelineStepState = {
        ...current[stepId],
        input,
        output: null,
        error: validation.success ? null : validation.error,
        status: validation.success ? 'ready' : 'error',
        streamTotal: 0,
        streamProgress: null,
        streamItems: [],
        streamMode: 'live',
      };

      return invalidateDownstreamSteps(
        {
          ...current,
          [stepId]: nextStep,
        },
        stepId,
        uploadsRef.current,
      );
    });

    if (notify) {
      message.success(t('pipelineDebug:messages.inputUpdated'));
    }
  };

  const setStepOutput = (stepId: PipelineStepId, output: unknown): void => {
    setSteps((current) => {
      const nextStep: PipelineStepState = {
        ...current[stepId],
        output,
        error: null,
        status: output === null || output === undefined ? 'ready' : 'completed',
      };

      return invalidateDownstreamSteps(
        {
          ...current,
          [stepId]: nextStep,
        },
        stepId,
        uploadsRef.current,
      );
    });

    message.success(t('pipelineDebug:messages.outputUpdated'));
  };

  const parseSelectedFileRecords = async (file: File): Promise<unknown[]> => {
    const content = await file.text();
    const lowerName = file.name.toLowerCase();

    if (lowerName.endsWith('.json')) {
      const parsed = parseJsonFileContent(file.name, content);
      if ('error' in parsed) {
        throw new Error(parsed.error);
      }

      return parsed.records;
    }

    return content
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        try {
          return JSON.parse(line) as unknown;
        } catch {
          return { message: line };
        }
      });
  };

  const ingestLinFile = async (file: File): Promise<void> => {
    if (!tryBeginExclusiveRun(runInFlightRef)) {
      return;
    }

    try {
      const records = await parseSelectedFileRecords(file);
      await clearSupervisorSandbox();

      const nextUploads: ParsedJsonUpload[] = [
        {
          id: crypto.randomUUID(),
          fileName: file.name,
          records,
        },
      ];

      setSelectedFile(file);
      setUploads(nextUploads);
      setResult(null);
      setActiveStepIndex(0);
      setSteps((current) => {
        const linStep: PipelineStepState = {
          ...current.lin,
          status: 'ready',
          input: records,
          output: null,
          error: null,
          streamTotal: 0,
          streamProgress: null,
          streamItems: [],
          streamMode: 'live',
        };

        return invalidateDownstreamSteps(
          {
            ...createInitialSteps(),
            lin: linStep,
          },
          'lin',
          nextUploads,
        );
      });
    } finally {
      endExclusiveRun(runInFlightRef);
    }
  };

  const runLinViaUpload = async (sanitizedInput: unknown): Promise<unknown> => {
    const fileToUpload = buildSupervisorLinUploadFile(
      sanitizedInput,
      selectedFileRef.current?.name,
    );

    await clearSupervisorSandbox();
    const response = await uploadSupervisorManualLog(fileToUpload);
    setResult(response);

    const normalizedLogs = Array.isArray(response.normalizedOutput)
      ? response.normalizedOutput
      : [response.normalizedOutput];

    setSteps((current) => ({
      ...current,
      lin: {
        ...current.lin,
        streamTotal: normalizedLogs.length,
        streamProgress: null,
        streamMode: 'summary',
        streamItems: normalizedLogs.map((output, index) => ({
          index,
          total: normalizedLogs.length,
          logId: readLogId(output, index),
          output,
          receivedAt: Date.now(),
        })),
      },
    }));

    return response.normalizedOutput;
  };

  const runStep = async (stepId: PipelineStepId): Promise<boolean> => {
    let resolvedInput: unknown = null;
    let sanitizedInput: unknown = null;
    let abortedBecauseAlreadyRunning = false;

    setSteps((current) => {
      if (isAnyPipelineStepRunning(current)) {
        abortedBecauseAlreadyRunning = true;
        return current;
      }

      resolvedInput = resolvePipelineStepInput(
        current,
        stepId,
        uploadsRef.current,
      );

      try {
        sanitizedInput = toJsonSafeValue(resolvedInput);
      } catch {
        sanitizedInput = null;
      }

      return {
        ...current,
        [stepId]: {
          ...current[stepId],
          status: 'running',
          input: sanitizedInput,
          output: null,
          error: null,
          streamTotal: 0,
          streamProgress: null,
          streamItems: [],
          streamMode: stepId === 'lin' ? 'summary' : 'live',
        },
      };
    });

    if (abortedBecauseAlreadyRunning) {
      return false;
    }

    if (sanitizedInput === null && resolvedInput !== null) {
      setSteps((current) => ({
        ...current,
        [stepId]: {
          ...current[stepId],
          status: 'error',
          error: t('pipelineDebug:messages.invalidJson'),
        },
      }));

      return false;
    }

    const validation = validateStepInput(stepId, sanitizedInput);
    if (!validation.success) {
      setSteps((current) => ({
        ...current,
        [stepId]: {
          ...current[stepId],
          status: 'error',
          error: validation.error,
        },
      }));
      return false;
    }

    try {
      let output: unknown;
      let streamError: string | null = null;

      if (stepId === 'lin') {
        output = await runLinViaUpload(sanitizedInput);
      } else {
        const streamHandlers = createStepStreamHandlers(stepId, setSteps);
        output = await runSupervisorPipelineStepStream(stepId, sanitizedInput, {
          ...streamHandlers,
          onError: (errorMessage) => {
            streamError = errorMessage;
          },
        });

        if (streamError) {
          throw new Error(streamError);
        }
      }

      setSteps((current) =>
        invalidateDownstreamSteps(
          {
            ...current,
            [stepId]: {
              ...current[stepId],
              status: 'completed',
              input: sanitizedInput,
              output: output ?? current[stepId].output,
              error: null,
              streamProgress: null,
            },
          },
          stepId,
          uploadsRef.current,
        ),
      );

      message.success(
        t('pipelineDebug:messages.stepCompleted', {
          step: t(`pipelineDebug:steps.${stepId}.title`),
          summary: summarizeStepOutput(stepId, output),
        }),
      );

      return true;
    } catch (error) {
      setSteps((current) => ({
        ...current,
        [stepId]: {
          ...current[stepId],
          status: 'error',
          input: sanitizedInput,
          error:
            error instanceof Error
              ? error.message
              : t('pipelineDebug:messages.unknownError'),
        },
      }));

      return false;
    }
  };

  const runFromActiveStep = async (): Promise<void> => {
    if (!tryBeginExclusiveRun(runInFlightRef)) {
      return;
    }

    try {
      const startIndex = activeStepIndex;

      for (
        let index = startIndex;
        index < PIPELINE_STEP_IDS.length;
        index += 1
      ) {
        const stepId = PIPELINE_STEP_IDS[index];
        setActiveStepIndex(index);
        const succeeded = await runStep(stepId);
        if (!succeeded) {
          break;
        }

        if (runMode === 'step-by-step') {
          if (index < PIPELINE_STEP_IDS.length - 1) {
            setActiveStepIndex(index + 1);
          }
          break;
        }
      }
    } finally {
      endExclusiveRun(runInFlightRef);
    }
  };

  const resetSandbox = async (): Promise<void> => {
    if (isResetting || !tryBeginExclusiveRun(runInFlightRef)) {
      return;
    }

    setIsResetting(true);

    try {
      await clearSupervisorSandbox();
      setUploads([]);
      setSelectedFile(null);
      setResult(null);
      setActiveStepIndex(0);
      setSteps(createInitialSteps());
      message.success(t('supervisor.resetSandboxSuccess'));
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : t('supervisor.resetSandboxFailed'),
      );
    } finally {
      endExclusiveRun(runInFlightRef);
      setIsResetting(false);
    }
  };

  const continuePipeline = async (): Promise<void> => {
    if (
      activeStepIndex >= PIPELINE_STEP_IDS.length - 1 ||
      !tryBeginExclusiveRun(runInFlightRef)
    ) {
      return;
    }

    try {
      const nextIndex = activeStepIndex + 1;
      setActiveStepIndex(nextIndex);
      await runStep(PIPELINE_STEP_IDS[nextIndex]);
    } finally {
      endExclusiveRun(runInFlightRef);
    }
  };

  const canRun =
    !isBusy &&
    (activeStep.input != null ||
      (activeStepId === 'lin' && uploads.length > 0) ||
      (activeStepIndex > 0 &&
        steps[PIPELINE_STEP_IDS[activeStepIndex - 1]].output != null));

  return {
    runMode,
    activeStepIndex,
    setActiveStepIndex,
    activeStepId,
    activeStep,
    uploads,
    steps,
    result,
    canRun,
    isBusy,
    isResetting,
    ingestLinFile,
    runFromActiveStep,
    continuePipeline,
    resetSandbox,
    setStepInput,
    setStepOutput,
  };
}
